/**
 * Data layer for the recipe gallery (recipes feature, Phase 1).
 *
 * Loads the recipes the current user can see (built-ins plus their orgs'
 * visible recipes) and groups them for the gallery's section bands:
 *  - `builtins` - made by holito (global, organization_id null)
 *  - `team`     - user recipes a teammate authored (shared into the org)
 *  - `mine`     - user recipes the current user authored
 *
 * Create / update / archive delegate to {@link services/recipeService} (RLS
 * enforces every write) and keep the local list in sync so the gallery updates
 * without a refetch. State conventions mirror {@link useCaseStudies}.
 *
 * @module components/hooks/useRecipes
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  listRecipes,
  createRecipe,
  updateRecipe,
  archiveRecipe,
  type Recipe,
  type RecipeVisibility,
  type UpdateRecipeInput,
} from '../../services/recipeService';
import { useSupabase } from '../../lib/SupabaseProvider';
import { captureException } from '../../lib/telemetry/posthog/frontend';

/** Inputs for {@link useRecipes}. */
export interface UseRecipesArgs {
  /** Active org id, required to create a recipe (null disables creation). */
  organizationId?: string | null;
  /** Display name stamped onto recipes this user creates. */
  authorName?: string | null;
}

/** The user-facing fields the editor collects when creating a recipe. */
export interface CreateRecipeFields {
  /** Match previous entries in this team. */
  crossCheckIdentity?: boolean;
  /** Research public context with citations. */
  useWebResearch?: boolean;
  /** Display name. */
  name: string;
  /** Optional author-editable one-line description. */
  description?: string;
  /** The natural-language generation instruction. */
  prompt: string;
  /** Sharing scope: Private (creator only) or Team. */
  visibility: RecipeVisibility;
  /** Free-form personal tags to attach to the new recipe. Defaults to [] when omitted. */
  tags?: string[];
}

/** Return contract of {@link useRecipes}. */
export interface UseRecipesResult {
  /** Built-in recipes made by holito. */
  builtins: Recipe[];
  /** User recipes a teammate authored. */
  team: Recipe[];
  /** User recipes the current user authored. */
  mine: Recipe[];
  /**
   * Sorted, de-duplicated union of all tags across the current user's own
   * recipes. Used to populate tag-filter options in the gallery.
   */
  myTags: string[];
  /** Whether the initial load is in flight. */
  loading: boolean;
  /** Load/mutation error message, or null. */
  error: string | null;
  /** Create a conversation/html/neutral user recipe in the active org. */
  create: (fields: CreateRecipeFields) => Promise<Recipe>;
  /** Edit a user recipe's name, prompt, or visibility. */
  update: (id: string, patch: UpdateRecipeInput) => Promise<Recipe>;
  /** Soft-delete a user recipe. */
  archive: (id: string) => Promise<void>;
}

/**
 * Owns the recipe gallery data layer: load + group + CRUD. See the module docs
 * for the grouping rules.
 */
export function useRecipes({ organizationId, authorName }: UseRecipesArgs): UseRecipesResult {
  const { supabase, user } = useSupabase();
  const userId = user?.id ?? null;

  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!supabase) return undefined;

    let cancelled = false;
    setLoading(true);
    listRecipes(supabase, organizationId)
      .then((rows) => {
        if (!cancelled) {
          setRecipes(rows);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load recipes');
        captureException(
          err instanceof Error ? err : new Error('Failed to load recipes'),
          'useRecipes.load',
          {},
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // Refetch when the active org changes so a switch loads that org's recipes
    // (and only that org's), never the prior org's.
  }, [supabase, organizationId]);

  const create = useCallback(
    async (fields: CreateRecipeFields): Promise<Recipe> => {
      if (!organizationId) {
        throw new Error('An organization is required to create a recipe');
      }
      try {
        const created = await createRecipe(supabase, {
          name: fields.name,
          crossCheckIdentity: fields.crossCheckIdentity,
          useWebResearch: fields.useWebResearch,
          description: fields.description ?? null,
          prompt: fields.prompt,
          scope: 'conversation',
          outputKind: 'html',
          rubric: 'neutral',
          visibility: fields.visibility,
          organizationId,
          authorName: authorName ?? null,
          tags: fields.tags ?? [],
        });
        setRecipes((prev) => [created, ...prev]);
        setError(null);
        return created;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to create recipe');
        captureException(
          err instanceof Error ? err : new Error('Failed to create recipe'),
          'useRecipes.create',
          { organizationId },
        );
        throw err;
      }
    },
    [supabase, organizationId, authorName],
  );

  const update = useCallback(
    async (id: string, patch: UpdateRecipeInput): Promise<Recipe> => {
      try {
        const updated = await updateRecipe(supabase, id, patch);
        setRecipes((prev) => prev.map((r) => (r.id === id ? updated : r)));
        setError(null);
        return updated;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update recipe');
        captureException(
          err instanceof Error ? err : new Error('Failed to update recipe'),
          'useRecipes.update',
          { recipeId: id },
        );
        throw err;
      }
    },
    [supabase],
  );

  const archive = useCallback(
    async (id: string): Promise<void> => {
      try {
        await archiveRecipe(supabase, id);
        setRecipes((prev) => prev.filter((r) => r.id !== id));
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to archive recipe');
        captureException(
          err instanceof Error ? err : new Error('Failed to archive recipe'),
          'useRecipes.archive',
          { recipeId: id },
        );
        throw err;
      }
    },
    [supabase],
  );

  const { builtins, team, mine, myTags } = useMemo(() => {
    const b: Recipe[] = [];
    const t: Recipe[] = [];
    const m: Recipe[] = [];
    for (const r of recipes) {
      if (r.isBuiltin) {
        b.push(r);
      } else if (userId && r.createdBy === userId) {
        m.push(r);
      } else {
        t.push(r);
      }
    }
    // Sorted, de-duplicated union of all tags across own recipes.
    const tagSet = new Set<string>();
    for (const r of m) {
      for (const tag of r.tags) {
        tagSet.add(tag);
      }
    }
    const computedMyTags = [...tagSet].sort();
    return { builtins: b, team: t, mine: m, myTags: computedMyTags };
  }, [recipes, userId]);

  return { builtins, team, mine, myTags, loading, error, create, update, archive };
}
